import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';
import {
    VERIFICATION_RESULT,
    VERIFICATION_STATUS,
    MIC_FAILURE_TYPES,
} from '../TROInstructionsConstants';
import ShapedButton from '../../../../common/components/ShapedButton';
import RecordingContainer from '../../../../common/recording/RecordingContainer';
import GoButtonDark from '../../../../common/components/GoButtonDark';
import { ARIA_LABELS } from '../../../../constants/text';

/*
    Max recording time we can send to soapbox's verification API is 30s.
    See: https://docs.soapboxlabs.com/technical-docs/online-technical-documentation/verification-(online)/
*/
const MAX_RECORDING_TIME_MS = 30000;

class MicCard extends Component {

    constructor(props) {
        super(props);

        this.state = {
            isOverlayActive: true,
            shouldStartRecording: false,
            shouldStopRecording: false,
        };
    }

    // eslint-disable-next-line consistent-return
    componentDidUpdate(prevProps) {
        const {
            verificationStatus,
            isSpinnerVisible,
            recordingStream,
            onFailure,
        } = this.props;
        const hasStatusChanged = verificationStatus !== prevProps.verificationStatus;
        const hasSpinnerVisibilityChanged = isSpinnerVisible !== prevProps.isSpinnerVisible;

        const isPassed = this.isPassed();

        if (hasSpinnerVisibilityChanged && isSpinnerVisible) this.setOverlayToInactive();

        if (
            hasSpinnerVisibilityChanged &&
            !isSpinnerVisible &&
            !isPassed
        )
            this.setOverlayToActive();

        if (
            hasStatusChanged &&
            isPassed
        ) {
            this.setOverlayToInactive();
        }

        if (recordingStream && !recordingStream.active)
            return onFailure(MIC_FAILURE_TYPES.ACCESS_FAILED);

        if (hasStatusChanged && verificationStatus === VERIFICATION_STATUS.ERROR) {
            onFailure(MIC_FAILURE_TYPES.MIC_TEST_ERROR);
        }

    }

    componentWillUnmount() {
        this.props.cleanup();
    }

    setOverlayToInactive = () => this.setState({ isOverlayActive: false });

    setOverlayToActive = () => this.setState({ isOverlayActive: true });

    isPassed = () => {
        const { verificationResult, isPassed } = this.props;
        return isPassed || verificationResult === VERIFICATION_RESULT.PASSED;
    }

    onStopRecordingClick = isExternalURLSourcePresent => {
        this.props.onStopRecording(isExternalURLSourcePresent);
        this.setState({ shouldStartRecording: false, shouldStopRecording: true });
    }

    onStartRecordingClick = isExternalURLSourcePresent => {
        const { updateVerificationProgress } = this.props;
        updateVerificationProgress(isExternalURLSourcePresent);
        this.setState({
            shouldStartRecording: true,
            shouldStopRecording: false,
        });
    };

    isVisualizerActive = () =>
        this.props.isRecordingInProgress && this.props.recordingStream;

    render = () => {
        const {
            isRecordingInProgress,
            isScreenBlocked,
            saveRecordedResult,
            isSpinnerVisible,
            onArrowButtonClick,
            children,
            shouldReinitializeRecorder,
            hideArrowButton,
            isExternalURLSourcePresent,
        } = this.props;
        const {
            isOverlayActive,
            shouldStartRecording,
            shouldStopRecording,
        } = this.state;
        const isPassed = this.isPassed();
        return (
            // eslint-disable-next-line react/jsx-fragments
            <React.Fragment>
                {isOverlayActive && <div className="reading-overlay" />}
                <div className="mic-test-card">
                    <RecordingContainer
                        duration={MAX_RECORDING_TIME_MS}
                        shouldStartRecording={shouldStartRecording}
                        shouldStopRecording={shouldStopRecording}
                        saveRecordedResult={saveRecordedResult}
                        shouldReinitializeRecorder={shouldReinitializeRecorder}
                    />
                    {!isRecordingInProgress && !isSpinnerVisible && isOverlayActive && (
                        <ShapedButton
                            ariaLabel={ARIA_LABELS.START_RECORDING}
                            onClick={() => this.onStartRecordingClick(isExternalURLSourcePresent)}
                            text="Start recording"
                            styles={classNames('start-recording-button', {
                                disabled: isScreenBlocked,
                            })}
                            isEnabled={!isScreenBlocked}
                        />
                    )}
                    {isRecordingInProgress && isOverlayActive && (
                        <ShapedButton
                            ariaLabel={ARIA_LABELS.STOP_RECORDING}
                            onClick={() =>  this.onStopRecordingClick(isExternalURLSourcePresent)}
                            text="Stop recording"
                            styles="stop-recording-button"
                            isEnabled={!isScreenBlocked}
                        />
                    )}
                    {children}
                </div>
                {!hideArrowButton && isPassed && (
                    <GoButtonDark
                        ariaLabel={ARIA_LABELS.GO_TO_NEXT_STEP}
                        onClick={onArrowButtonClick}
                        isPulsating={!isScreenBlocked}
                        isEnabled={!isScreenBlocked}
                    />
                )}
            </React.Fragment>
        );
    };
}

MicCard.propTypes = {
    recordingStream: PropTypes.oneOfType([PropTypes.bool, PropTypes.instanceOf(MediaStream)]),
    isRecordingInProgress: PropTypes.bool.isRequired,
    isScreenBlocked: PropTypes.bool.isRequired,
    verificationResult: PropTypes.string,
    onArrowButtonClick: PropTypes.func,
    verificationStatus: PropTypes.string,
    isSpinnerVisible: PropTypes.bool.isRequired,
    cleanup: PropTypes.func.isRequired,
    updateVerificationProgress: PropTypes.func,
    onStopRecording: PropTypes.func,
    saveRecordedResult: PropTypes.func.isRequired,
    onFailure: PropTypes.func.isRequired,
    children: PropTypes.node,
    shouldReinitializeRecorder: PropTypes.bool,
    hideArrowButton: PropTypes.bool,
    isPassed: PropTypes.bool,
    isExternalURLSourcePresent: PropTypes.bool.isRequired,
};

MicCard.defaultProps = {
    recordingStream: false,
    // eslint-disable-next-line react/jsx-fragments
    children: <React.Fragment />,
    shouldReinitializeRecorder: false,
    updateVerificationProgress: () => {},
    onStopRecording: () => {},
    onArrowButtonClick: () => {},
    hideArrowButton: false,
    verificationStatus: VERIFICATION_STATUS.IDLE,
    isPassed: false,
    verificationResult: VERIFICATION_RESULT.UNAVAILABLE
};

export default MicCard;
